<template>
  <v-autocomplete
      :error-messages="error"
      variant="outlined"
      rounded="lg"
      bg-color="surface"
      :label="translation(translationList.vehicle_make, 'Brand')"
      :items="vehicleMakes"
      name="make"
      item-title="name"
      item-value="id"
      v-model="selectedMake"
      :rules="[v => !!v || translation(translationList.required, 'Acest camp este obligatoriu')]"
      :loading="loading"
      :disabled="loading"
      return-object
      :menu-props="{ eager: true }"
  >
    <template v-slot:append-inner>
      <v-tooltip
          location="top"
          open-delay="500"
          open-on-click open-on-hover
      >
        <template v-slot:activator="{ props }">
          <v-icon v-bind="props"  class="cursor-pointer">
            mdi-information
          </v-icon>
        </template>
        <span><NuxtImg
            src="/images/vehicle/registration_template/template_d1.png"
            class="h-100"
            loading="lazy"
            alt="Registration template"
        /></span>
      </v-tooltip>
    </template>
  </v-autocomplete>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, watch, toRefs } from 'vue';
import { useVehicleStore } from '~/plugins/store/vehicle';
import { useTranslationStore, translation } from '~/plugins/store/translations';
import type {VehicleMake} from "~/types/Vehicle";
import type {TranslationList} from "~/types/Utils";

export default defineComponent({
  props: {
    modelValue: {
      type: Object as () => VehicleMake | null,
      default: () => null,
      required: false
    },
    vehicleCategoryWsId: {
      type: Number as () => number | null,
      required: true
    },
  },
  emits: ['update:modelValue', 'validation-vehicle-status'],
  setup(props, { emit }) {
    const { modelValue, vehicleCategoryWsId } = toRefs(props);
    const vehicleStore = useVehicleStore();
    const translationStore = useTranslationStore();
    const selectedMake = ref(modelValue.value ? modelValue.value : null);
    const vehicleMakes = ref<VehicleMake[]>([]);
    const translationList = ref<TranslationList>({});
    const loading = ref(false);
    let initialSetupDone = false;
    const error = ref([]);

    const fetchVehicleMakes = async (categoryWsId: number) => {
      loading.value = true;
      if (categoryWsId) {
        vehicleMakes.value = await vehicleStore.getVehicleMakes(categoryWsId);
      } else {
        vehicleMakes.value = [];
        selectedMake.value = null;
        emit('update:modelValue', null);
      }
      loading.value = false;
    };

    onMounted(async () => {
      translationList.value = await translationStore.getTranslations('ro', 'dashboard');
      if (vehicleCategoryWsId.value) {
        await fetchVehicleMakes(vehicleCategoryWsId.value);
      }
      initialSetupDone = true;

      // Listen for validate event
      window.addEventListener('validate-vehicle', (data) => {
        if (data.detail && data.detail === 'make') {
          if (!selectedMake.value) {
            error.value.push(translation(translationList.value.required, 'Acest câmp este obligatoriu'));
          } else {
            error.value = [];
            emit('validation-vehicle-status', { field: 'make', isValid: true });
          }
        }
      });
    });

    watch(vehicleCategoryWsId, async (newCategoryWsId) => {
      if (modelValue.value && modelValue.value.rca_mapped_category &&
          modelValue.value.rca_mapped_category.ws_id === newCategoryWsId) {
        selectedMake.value = modelValue.value;
      } else {
        selectedMake.value = null;
        emit('update:modelValue', null);
      }
      if (newCategoryWsId) {
        await fetchVehicleMakes(newCategoryWsId);
      }
    });

    watch(selectedMake, (newMake) => {
      if (initialSetupDone ) {
        emit('update:modelValue', newMake);
      }
    });

    watch(modelValue, (newValue) => {
      if (!newValue) {
        selectedMake.value = null;
      } else if (newValue) {
        selectedMake.value = newValue;
        error.value = [];
      }
      const isValid = !!newValue;
      emit('validation-vehicle-status', { field: 'make', isValid });
    });

    return {
      selectedMake,
      translationList,
      translation,
      vehicleMakes,
      loading,
      error,
    };
  },
});
</script>

<style scoped>
</style>
